import { useQueries } from '@tanstack/react-query';

const DOWNLOAD_FILE_KEY = 'downloadFile';
/*
 * Generic hook to download a file blob from signed URLs.
 * Use enabled flag to control when to fetch the blob with more control than just the presence of the signed URLs payload.
 */
export const useGetBlobFilesFromStorage = (
  signedUrls: Record<string, string>,
  enabled: boolean = true,
) => {
  return useQueries({
    queries: Object.entries(signedUrls).map(([databaseUrl, signedUrl]) => ({
      /*
       * Query key needs to differ from useGetBlobFileFromStorage query key
       * as the return type is different, which is needed to keep the connection to the database URL.
       */
      queryKey: ['batch', DOWNLOAD_FILE_KEY, signedUrl],
      queryFn: async () => {
        if (!signedUrl) throw new Error('No signed URL provided');

        try {
          // use regular fetch as authentication is handled via signed URL instead of axios instance
          const response = await fetch(signedUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.statusText}`);
          }

          return { [databaseUrl]: await response.blob() };
        } catch (error) {
          if (error instanceof Error) {
            throw new Error(`Failed to download file: ${error.message}`);
          }
          throw new Error('Failed to download file: Unknown error');
        }
      },
      enabled: !!signedUrl && enabled,
    })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        pending: results.some((result) => result.isPending),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });
};
